import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    MapPin,
    Calendar,
    Filter,
    ArrowUpRight,
    Sprout,
    X,
    Images,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import SectionHeading from "@/components/SectionHeading";
import CategoryFilter from "@/components/CategoryFilter";
import OrganicShapes from "@/components/OrganicShapes";
import { GALLERY_ITEMS, CATEGORIES } from "@/lib/mockData";
import { fetchGallery } from "@/lib/api";
import { USE_MOCK } from "@/lib/config";
import { CAUSE_LABEL } from "@/lib/causes";
import { formatDate } from "@/lib/date";

const FILTERS = [
    { key: "all", label: "All" },
    { key: "trees", label: "Tree Plantation" },
    { key: "food", label: "Food Drives" },
    { key: "education", label: CAUSE_LABEL.education },
    { key: "community", label: "Community Activity" },
];

const COMMUNITY_CAT = {
    label: "Community Activity",
    short: "Community",
    accentText: "text-seedh-brown",
    accentBg: "bg-seedh-brown",
};

function categoryMeta(key) {
    return CATEGORIES[key] || COMMUNITY_CAT;
}

export default function Gallery() {
    const [active, setActive] = useState("all");
    const [open, setOpen] = useState(null);          // selected drive
    const [lightbox, setLightbox] = useState(null);  // { photos, index }
    const [items, setItems] = useState(USE_MOCK ? GALLERY_ITEMS : []);

    // Load gallery from the backend. In production we don't fall back to mock
    // items — an unavailable/empty backend shows the empty state, not fakes.
    useEffect(() => {
        let alive = true;
        fetchGallery()
            .then((data) => {
                if (alive && Array.isArray(data)) setItems(data);
            })
            .catch(() => {
                if (alive && USE_MOCK) setItems(GALLERY_ITEMS);
            });
        return () => {
            alive = false;
        };
    }, []);

    // Lock body scroll while a modal is open
    useEffect(() => {
        const lock = open || lightbox;
        document.body.style.overflow = lock ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open, lightbox]);

    // Lightbox keyboard navigation
    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e) => {
            if (e.key === "Escape") setLightbox(null);
            if (e.key === "ArrowRight")
                setLightbox((s) => ({
                    ...s,
                    index: (s.index + 1) % s.photos.length,
                }));
            if (e.key === "ArrowLeft")
                setLightbox((s) => ({
                    ...s,
                    index: (s.index - 1 + s.photos.length) % s.photos.length,
                }));
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox]);

    const filtered = useMemo(
        () =>
            active === "all"
                ? items
                : items.filter((i) => i.category === active),
        [active, items],
    );

    return (
        <main
            data-testid="gallery-page"
            className="bg-white pt-32 pb-24 relative overflow-hidden"
        >
            <OrganicShapes variant="beige" />

            <section className="max-w-7xl mx-auto px-6 md:px-10 relative">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
                    <SectionHeading
                        eyebrow="Impact gallery"
                        heading={["Quiet moments,", "publicly remembered."]}
                        italicIndex={1}
                        description="An archive of drives, distributions, and community moments — across trees, food relief, and child welfare. Tap any drive to see its full photo set."
                    />

                    <SectionReveal delay={0.15}>
                        <div className="flex flex-col gap-3">
                            <span className="inline-flex items-center gap-1.5 text-xs text-seedh-muted">
                                <Filter className="w-3.5 h-3.5" />
                                Filter by category
                            </span>
                            <CategoryFilter
                                options={FILTERS}
                                active={active}
                                onChange={setActive}
                                testIdPrefix="gallery-filter"
                            />
                        </div>
                    </SectionReveal>
                </div>

                {/* Masonry grid */}
                <motion.div
                    layout
                    className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-[260px] gap-5"
                    data-testid="gallery-grid"
                >
                    <AnimatePresence mode="popLayout">
                        {filtered.map((item, i) => {
                            const cat = categoryMeta(item.category);
                            return (
                                <motion.article
                                    key={item.id}
                                    layout
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{
                                        duration: 0.6,
                                        ease: [0.22, 1, 0.36, 1],
                                        delay: i * 0.05,
                                    }}
                                    whileHover={{ y: -6 }}
                                    onClick={() => setOpen(item)}
                                    data-testid={`gallery-item-${item.id}`}
                                    className={`gallery-card relative card-base overflow-hidden cursor-pointer group ${item.span || ""}`}
                                >
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="gallery-img absolute inset-0 w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-seedh-text/85 via-seedh-text/20 to-transparent" />

                                    <div className={`absolute top-4 left-4 pill bg-white/95 backdrop-blur ${cat.accentText}`}>
                                        {cat.label || cat.short}
                                    </div>
                                    {item.photos?.length > 1 && (
                                        <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[0.65rem] font-semibold text-seedh-blue">
                                            <Images className="w-3.5 h-3.5" />
                                            {item.photos.length}
                                        </div>
                                    )}

                                    <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                                        <h3 className="font-display text-2xl leading-tight">
                                            {item.title}
                                        </h3>
                                        <div className="mt-3 flex items-center gap-4 text-[0.72rem] text-white/80">
                                            <span className="inline-flex items-center gap-1.5">
                                                <Calendar className="w-3.5 h-3.5" />
                                                {formatDate(item.date)}
                                            </span>
                                            <span className="inline-flex items-center gap-1.5">
                                                <MapPin className="w-3.5 h-3.5" />
                                                {item.location}
                                            </span>
                                        </div>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </AnimatePresence>
                </motion.div>

                {filtered.length === 0 && (
                    <SectionReveal>
                        <div
                            data-testid="gallery-empty-state"
                            className="mt-12 rounded-2xl border border-dashed border-seedh-border p-12 text-center bg-seedh-surface-beige/40"
                        >
                            <Sprout className="w-8 h-8 text-seedh-green mx-auto" />
                            <div className="font-display text-2xl text-seedh-text mt-4">
                                Nothing here yet — soon, though.
                            </div>
                            <p className="text-sm text-seedh-muted mt-2 max-w-md mx-auto">
                                This category will fill up as the foundation
                                publishes new field updates.
                            </p>
                        </div>
                    </SectionReveal>
                )}

                <SectionReveal delay={0.1}>
                    <div className="mt-16 text-center text-[0.72rem] tracking-[0.32em] uppercase text-seedh-muted">
                        Items shown in reverse chronological order
                    </div>
                </SectionReveal>
            </section>

            {/* DRIVE DETAIL MODAL — photo grid */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => setOpen(null)}
                        data-testid="gallery-modal"
                        className="fixed inset-0 z-[60] bg-seedh-text/80 backdrop-blur-md flex items-start justify-center p-3 md:p-8 overflow-y-auto"
                    >
                        <motion.div
                            initial={{ y: 40, opacity: 0, scale: 0.97 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 30, opacity: 0, scale: 0.98 }}
                            transition={{
                                duration: 0.55,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl overflow-hidden max-w-5xl w-full my-auto relative"
                        >
                            {/* Header bar */}
                            <div className="relative px-6 md:px-10 pt-8 md:pt-10 pb-6 md:pb-8 border-b border-seedh-border">
                                <button
                                    onClick={() => setOpen(null)}
                                    data-testid="gallery-modal-close"
                                    className="absolute top-5 right-5 w-10 h-10 rounded-full bg-seedh-surface-blue border border-seedh-border flex items-center justify-center text-seedh-blue hover:bg-seedh-blue hover:text-white transition-colors z-10"
                                    aria-label="Close"
                                >
                                    <X className="w-4 h-4" />
                                </button>

                                <div className={`pill bg-white border ${categoryMeta(open.category).accentText} border-seedh-border w-fit`}>
                                    {categoryMeta(open.category).label || categoryMeta(open.category).short}
                                </div>
                                <h3 className="font-display text-3xl md:text-4xl text-seedh-blue mt-4 leading-tight pr-12">
                                    {open.title}
                                </h3>
                                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-seedh-muted">
                                    <span className="inline-flex items-center gap-1.5">
                                        <Calendar className="w-3.5 h-3.5" />
                                        {formatDate(open.date)}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin className="w-3.5 h-3.5" />
                                        {open.location}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 text-seedh-blue">
                                        <Images className="w-3.5 h-3.5" />
                                        {(open.photos || [open.image]).length} photos
                                    </span>
                                </div>
                                <p className="mt-5 text-base text-seedh-text leading-relaxed max-w-3xl">
                                    {open.description}
                                </p>
                            </div>

                            {/* Photo grid */}
                            <motion.div
                                initial="hidden"
                                animate="show"
                                variants={{
                                    show: {
                                        transition: { staggerChildren: 0.06 },
                                    },
                                }}
                                className="p-6 md:p-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4"
                            >
                                {(open.photos || [open.image]).map((p, idx) => (
                                    <motion.button
                                        key={`${open.id}-${idx}`}
                                        variants={{
                                            hidden: {
                                                opacity: 0,
                                                y: 24,
                                                scale: 0.94,
                                            },
                                            show: {
                                                opacity: 1,
                                                y: 0,
                                                scale: 1,
                                                transition: {
                                                    duration: 0.55,
                                                    ease: [0.22, 1, 0.36, 1],
                                                },
                                            },
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() =>
                                            setLightbox({
                                                photos: open.photos || [open.image],
                                                index: idx,
                                            })
                                        }
                                        data-testid={`gallery-photo-${open.id}-${idx}`}
                                        className={`relative overflow-hidden rounded-2xl border border-seedh-border group ${
                                            idx === 0 ? "col-span-2 md:col-span-2 row-span-2 aspect-[4/3] md:aspect-auto md:min-h-[320px]" : "aspect-square"
                                        }`}
                                    >
                                        <img
                                            src={p}
                                            alt={`${open.title} — photo ${idx + 1}`}
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-seedh-text/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <div className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-seedh-blue opacity-0 group-hover:opacity-100 transition-opacity">
                                            <ArrowUpRight className="w-4 h-4" />
                                        </div>
                                    </motion.button>
                                ))}
                            </motion.div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* LIGHTBOX */}
            <AnimatePresence>
                {lightbox && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => setLightbox(null)}
                        data-testid="gallery-lightbox"
                        className="fixed inset-0 z-[70] bg-[#040a10]/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
                    >
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setLightbox(null);
                            }}
                            data-testid="lightbox-close"
                            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 border border-white/15 text-white flex items-center justify-center hover:bg-white/20 transition-colors z-10"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setLightbox((s) => ({
                                    ...s,
                                    index: (s.index - 1 + s.photos.length) % s.photos.length,
                                }));
                            }}
                            data-testid="lightbox-prev"
                            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/15 text-white flex items-center justify-center hover:bg-white/20 transition-colors z-10"
                            aria-label="Previous"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setLightbox((s) => ({
                                    ...s,
                                    index: (s.index + 1) % s.photos.length,
                                }));
                            }}
                            data-testid="lightbox-next"
                            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/15 text-white flex items-center justify-center hover:bg-white/20 transition-colors z-10"
                            aria-label="Next"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>

                        <motion.img
                            key={lightbox.index}
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            src={lightbox.photos[lightbox.index]}
                            alt={`Photo ${lightbox.index + 1}`}
                            onClick={(e) => e.stopPropagation()}
                            className="max-w-[92vw] max-h-[82vh] object-contain rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
                        />

                        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.2em] uppercase font-mono">
                            {lightbox.index + 1} / {lightbox.photos.length}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </main>
    );
}
